import { useCallback, useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSocketStore } from "app/store/socketStore";

type SalesRoom = {
	_id: string | null;
	shipment: string;
	customer_supplier_id: string;
	last_message_text: string;
	last_message_source: string;
	last_message_datetime: string | null;
};

/**
 * Conversation list for sales mode with multiple scoped shipments (a rep's
 * book). The list comes from the token's scope via the server — selecting an
 * item joins that shipment's sales thread.
 */
export function SalesChatSidebar() {
	const { t: trl } = useTranslation();
	const { messageSocket, isConnected } = useSocketStore();
	const [rooms, setRooms] = useState<SalesRoom[]>([]);
	const [selected, setSelected] = useState<string>("");

	const onRooms = useCallback((data: SalesRoom[]) => setRooms(data || []), []);

	useEffect(() => {
		if (!messageSocket || !messageSocket.connected) return undefined;

		const refresh = () => messageSocket.emit("sales-chat-rooms::fetch");
		messageSocket.on("sales-chat-rooms", onRooms);
		messageSocket.on("messages-updated", refresh);
		refresh();

		return () => {
			messageSocket.off("sales-chat-rooms", onRooms);
			messageSocket.off("messages-updated", refresh);
		};
	}, [messageSocket, isConnected, onRooms]);

	const openRoom = (shipment: string) => {
		setSelected(shipment);
		messageSocket?.emit("sales-chat::join", { shipment });
	};

	return (
		<div className="flex flex-col w-[300px] min-w-[300px] h-full border-r border-gray-200 bg-white overflow-hidden">
			<div className="px-16 py-12 border-b border-gray-200">
				<Typography className="text-[15px] font-semibold text-[color:var(--rc-ink,#111827)]">
					{trl("comms_salesConversation")}
				</Typography>
			</div>
			<div className="flex-1 overflow-y-auto">
				{rooms.map((room) => (
					<button
						key={room.shipment}
						type="button"
						onClick={() => openRoom(room.shipment)}
						className={`w-full text-left px-16 py-10 border-b border-gray-100 cursor-pointer ${
							selected === room.shipment
								? "bg-[color:var(--rc-accent-faint,#EBF2FE)]"
								: "bg-white hover:bg-[color:var(--rc-hover,#F5F5F5)]"
						}`}
					>
						<div className="flex items-center justify-between gap-8">
							<Typography className="text-[13px] font-semibold text-gray-900 truncate">
								{room.shipment}
							</Typography>
							{room.last_message_datetime && (
								<Typography className="text-[10px] text-gray-500 shrink-0">
									{dayjs(room.last_message_datetime).format("MMM D, hh:mm A")}
								</Typography>
							)}
						</div>
						{room.customer_supplier_id && (
							<Typography className="text-[11px] text-gray-600">
								{room.customer_supplier_id}
							</Typography>
						)}
						<Typography className="text-[11.5px] text-gray-500 truncate">
							{room.last_message_text || trl("comms_noChats")}
						</Typography>
					</button>
				))}
			</div>
		</div>
	);
}

export default SalesChatSidebar;
